<script setup lang="ts">
    import _ from 'lodash';
    import { computed } from 'vue';
    import { useSqlEditorStore } from '@/stores/sqlEditorStore.ts';
    import { http } from '@/shared/httpWrapper.js';
    import useBuefy from '@/shared/composables/useBuefy.ts';
    import useShow from '@/shared/composables/useShow.js';
    import { deleteSavedQuery } from '@/shared/helpers/api';
    import { useRouter } from 'vue-router';
    import { getSqlEditorLink } from '@/shared/helpers/routing';

    const store = useSqlEditorStore();
    const buefy = useBuefy();
    const router = useRouter();
    const { showInfo, showError } = useShow();

    const canDelete = computed(() =>
        /*
            Owner and administrators can delete queries,
            but only if it's not public.
         */
        (store.loadedQueryOwnedByCurrentUser || (store.queryIsLoaded && store.userIsAdministrator))
        && !store.loadedQueryOriginalIsPublic);

    async function onDeleteSavedQuery() {
        buefy.dialog.confirm({
            title: 'Please confirm delete',
            message: 'Are you sure you want to delete the saved query?',
            type: 'is-danger',
            hasIcon: true,
            onConfirm: async () => {
                if (store.loadedQueryId === null) {
                    showError('No query is loaded, can\'t delete!');
                    return;
                }

                try {
                    await deleteSavedQuery(http, store.loadedQueryId);
                    showInfo('Query deleted');
                    store.loadedQueryUnset();
                    await store.savedQueriesReload();
                    await router.push(getSqlEditorLink());
                } catch (error) {
                    showError(_.get(error, 'response.data.Message', 'Unable to delete item'));
                }
            }
        });
    }
</script>

<template>
    <button
        class="button"
        title="Delete saved query"
        :disabled="!canDelete"
        @click="onDeleteSavedQuery">
        <b-icon
            icon="delete"
            size="is-small" />
        <span>Delete</span>
    </button>
</template>
